class DashboardAddresses extends HTMLElement {
  connectedCallback() {
    this._config = this._readConfig();
    this._addresses = [];
    this._fetchList();
    this._bindGridClicks();
  }

  _readConfig() {
    const script = this.querySelector('[data-config-json]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  _escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }

  _fetchList() {
    const endpoint = this.dataset.endpoint || '/apps/lensza/addresses';

    fetch(endpoint)
      .then((r) => {
        if (!r.ok) throw new Error(`addresses fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        this._addresses = data.addresses || [];
        this._render();
      })
      .catch(() => {
        // Fetch failed — fall back to an empty list rather than shimmering forever.
        this._addresses = [];
        this._render();
      })
      .finally(() => {
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
      });
  }

  _render() {
    const grid = this.querySelector('[data-address-grid]');
    const emptyEl = this.querySelector('[data-address-empty-message]');
    if (!grid) return;

    if (this._addresses.length === 0) {
      grid.innerHTML = '';
      emptyEl?.classList.remove('hidden');
      return;
    }

    emptyEl?.classList.add('hidden');
    grid.innerHTML = this._addresses
      .map((addr) => this._cardHtml(addr))
      .join('');
  }

  _addressLines(addr) {
    const line1 = [addr.address1, addr.address2].filter(Boolean).join(', ');
    const cityParts = [addr.city, addr.province].filter(Boolean).join(', ');
    const cityZip = [cityParts, addr.zip].filter(Boolean).join(' ');
    const line2 = [cityZip, addr.country].filter(Boolean).join(', ');
    return { line1, line2 };
  }

  _cardHtml(addr) {
    const c = this._config;
    const { line1, line2 } = this._addressLines(addr);
    const recipient = [addr.first_name, addr.last_name]
      .filter(Boolean)
      .join(' ');

    const badgeHtml = addr.is_default
      ? `<span class="body-xxs font-bold uppercase tracking-wide rounded-full px-2.5 py-1 bg-primary/10 text-primary flex-none inline-flex items-center gap-1">${c.starIconSvg || ''}${this._escapeHtml(c.defaultBadgeLabel)}</span>`
      : '';

    const setDefaultBtnHtml = addr.is_default
      ? ''
      : `<button type="button" class="cursor-pointer inline-flex items-center gap-1.5 body-xs font-semibold rounded-full px-3 py-1.5 border border-border text-text-dark hover:border-primary hover:text-primary transition-colors" data-set-default data-address-id="${addr.id}">${c.starIconSvg || ''}${this._escapeHtml(c.setDefaultLabel)}</button>`;

    const phoneHtml = addr.phone
      ? `<div class="flex items-center gap-1.5 body-sm text-text-muted">${c.phoneIconSvg || ''}<span>${this._escapeHtml(addr.phone)}</span></div>`
      : '';

    const companyHtml = addr.company
      ? `<div class="body-xs text-text-muted -mt-2">${this._escapeHtml(addr.company)}</div>`
      : '';

    return `
      <div class="bg-background border border-border rounded-2xl shadow-sm p-4 flex flex-col gap-3" data-address-card data-address-id="${addr.id}">
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            ${c.mapPinIconSvg || ''}
            <span class="body-sm font-bold truncate">${this._escapeHtml(recipient)}</span>
          </div>
          ${badgeHtml}
        </div>

        ${companyHtml}

        <div class="body-sm text-text-muted leading-relaxed">
          ${this._escapeHtml(line1)}<br>${this._escapeHtml(line2)}
        </div>

        ${phoneHtml}

        <div class="flex items-center justify-between gap-2 pt-3 mt-auto border-t border-border">
          <div class="flex items-center gap-2">
            <button type="button" class="cursor-pointer inline-flex items-center gap-1.5 body-xs font-semibold rounded-full px-3 py-1.5 border border-border text-text-dark hover:border-primary hover:text-primary transition-colors" data-edit-address data-address-id="${addr.id}">
              ${c.pencilIconSvg || ''}${this._escapeHtml(c.editLabel)}
            </button>
            ${setDefaultBtnHtml}
          </div>
          <button type="button" class="cursor-pointer inline-flex items-center gap-1.5 body-xs font-semibold text-error hover:underline" data-delete-address data-address-id="${addr.id}">
            ${c.trashIconSvg || ''}${this._escapeHtml(c.deleteLabel)}
          </button>
        </div>
      </div>
    `;
  }

  _bindGridClicks() {
    // The primary "Add new address" button lives in the block's header row,
    // outside this custom element (see blocks/dashboard-addresses.liquid),
    // so the listener must go on the shared wrapper, not `this`.
    const wrapper = this.closest('[data-addresses-wrapper]') || this;
    wrapper.addEventListener('click', (e) => {
      if (e.target.closest('[data-open-add-address]')) {
        this._openDialog('new');
        return;
      }
      const editBtn = e.target.closest('[data-edit-address]');
      if (editBtn) {
        this._openDialog('edit', editBtn.dataset.addressId);
        return;
      }
      const defaultBtn = e.target.closest('[data-set-default]');
      if (defaultBtn) {
        this._setDefault(defaultBtn.dataset.addressId, defaultBtn);
        return;
      }
      const deleteBtn = e.target.closest('[data-delete-address]');
      if (deleteBtn) {
        this._deleteAddress(deleteBtn.dataset.addressId, deleteBtn);
      }
    });
  }

  _fieldRowHtml(fieldName, label, value, options = {}) {
    const { type = 'text', required = false } = options;
    const id = `address-${fieldName}`;
    return `
      <div>
        <label class="block body-sm font-medium form-field__label mb-1" for="${id}">${this._escapeHtml(label)}</label>
        <input id="${id}" type="${type}" value="${this._escapeHtml(value || '')}" class="w-full rounded-lg border transition-colors duration-200 form-field__input h-10 px-3 body-sm" data-field="${fieldName}" ${required ? 'required' : ''} />
      </div>
    `;
  }

  _dialogHtml(mode, address) {
    const c = this._config;
    const heading = mode === 'edit' ? c.editDialogHeading : c.addDialogHeading;
    const a = address || {};

    return `
      <div class="px-5 lg:px-8 py-6 md:py-8" data-address-dialog>
        <h3 class="font-heading font-bold text-xl mb-4">${this._escapeHtml(heading)}</h3>
        <form data-address-form>
          <p class="body-xs text-error mb-3 hidden" data-address-form-error></p>

          <div class="grid grid-cols-2 gap-3 mb-3">
            ${this._fieldRowHtml('first_name', c.firstNameLabel, a.first_name, { required: true })}
            ${this._fieldRowHtml('last_name', c.lastNameLabel, a.last_name, { required: true })}
          </div>
          <div class="mb-3">
            ${this._fieldRowHtml('company', c.companyLabel, a.company)}
          </div>
          <div class="mb-3">
            ${this._fieldRowHtml('address1', c.address1Label, a.address1, { required: true })}
          </div>
          <div class="mb-3">
            ${this._fieldRowHtml('address2', c.address2Label, a.address2)}
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            ${this._fieldRowHtml('city', c.cityLabel, a.city, { required: true })}
            ${this._fieldRowHtml('zip', c.zipLabel, a.zip, { required: true })}
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div data-country-slot></div>
            <div data-province-slot></div>
          </div>
          <div class="mb-4">
            ${this._fieldRowHtml('phone', c.phoneLabel, a.phone, { type: 'tel' })}
          </div>

          <label class="flex items-center gap-2 border border-border rounded-xl px-3 py-2.5 cursor-pointer body-sm mb-4" data-default-toggle-row>
            <input type="checkbox" class="sr-only" data-default-checkbox ${a.is_default ? 'checked' : ''}>
            <span class="w-5 h-5 rounded-full border border-border flex items-center justify-center flex-none" data-default-checkmark>
              <span class="${a.is_default ? '' : 'hidden'}" data-default-check-icon>${c.checkIconSvg || ''}</span>
            </span>
            ${this._escapeHtml(c.setDefaultLabel)}
          </label>

          <div class="flex gap-2">
            <button type="button" class="btn relative rounded-full btn--secondary-outline btn--md flex-1" data-dialog-close>
              <span class="btn-content w-max"><span class="btn-text">${this._escapeHtml(c.cancelLabel)}</span></span>
            </button>
            <button type="submit" class="btn relative rounded-full btn--primary btn--md flex-1" data-address-form-submit>
              <span class="btn-content w-max"><span class="btn-text">${this._escapeHtml(c.saveLabel)}</span></span>
              <span class="btn-spinner absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden">${c.spinnerIconSvg || ''}</span>
            </button>
          </div>
        </form>
      </div>
    `;
  }

  _openDialog(mode, addressId) {
    const dialogModal = document.getElementById('DialogModal');
    if (
      !dialogModal ||
      typeof dialogModal.setContent !== 'function' ||
      typeof dialogModal.openModal !== 'function'
    ) {
      return;
    }

    const address =
      mode === 'edit'
        ? this._addresses.find((a) => String(a.id) === String(addressId))
        : null;
    if (mode === 'edit' && !address) return;

    if (typeof dialogModal.setSize === 'function') dialogModal.setSize('small');

    dialogModal.setContent(this._dialogHtml(mode, address));
    dialogModal.openModal();

    const root = dialogModal.querySelector('[data-address-dialog]');
    if (!root) return;

    this._mountCountryProvince(root, address);
    this._bindDefaultToggle(root);
    this._bindDialogSubmit(root, dialogModal, mode, address);
  }

  _mountCountryProvince(root, address) {
    const countrySlot = root.querySelector('[data-country-slot]');
    const provinceSlot = root.querySelector('[data-province-slot]');
    const countryTemplate = this.querySelector(
      'template[data-country-select-template]',
    );
    const provinceTemplate = this.querySelector(
      'template[data-province-select-template]',
    );
    if (!countrySlot || !provinceSlot || !countryTemplate || !provinceTemplate)
      return;

    const countryEl = countryTemplate.content.firstElementChild.cloneNode(true);
    const provinceEl =
      provinceTemplate.content.firstElementChild.cloneNode(true);
    // Must be connected to the document before <input-select>'s connectedCallback
    // wires up select/trigger/dropdown and setValue() becomes usable — same
    // requirement as dashboard-profile-completeness.js's _mountPowerSelect.
    countrySlot.replaceWith(countryEl);
    provinceSlot.replaceWith(provinceEl);

    const countrySelect = countryEl.querySelector('select');
    const provinceSelect = provinceEl.querySelector('select');
    if (!countrySelect || !provinceSelect) return;

    const applyProvinces = (preselectProvince) => {
      const selectedOption = countrySelect.selectedOptions[0];
      let provinces = [];
      try {
        provinces = JSON.parse(selectedOption?.dataset.provinces || '[]');
      } catch {
        provinces = [];
      }

      provinceSelect.innerHTML = '';
      // Replacing the native <select>'s option children fires <input-select>'s
      // MutationObserver, which rebuilds its custom dropdown UI automatically —
      // confirmed in assets/input-select.js connectedCallback, no manual refresh needed.
      provinces.forEach(([name]) => {
        const opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        provinceSelect.appendChild(opt);
      });

      const provinceRow = provinceEl.closest('[data-form-field]') || provinceEl;
      provinceRow.classList.toggle('hidden', provinces.length === 0);

      if (preselectProvince && typeof provinceEl.setValue === 'function') {
        provinceEl.setValue(preselectProvince);
      }
    };

    countrySelect.addEventListener('change', () => applyProvinces(null));

    if (address?.country && typeof countryEl.setValue === 'function') {
      countryEl.setValue(address.country, { emitChange: false });
    }
    applyProvinces(address?.province || null);
  }

  _bindDefaultToggle(root) {
    const row = root.querySelector('[data-default-toggle-row]');
    const checkbox = root.querySelector('[data-default-checkbox]');
    const checkIcon = root.querySelector('[data-default-check-icon]');
    if (!row || !checkbox) return;

    const sync = () => {
      checkIcon?.classList.toggle('hidden', !checkbox.checked);
      row.classList.toggle('border-primary', checkbox.checked);
      row.classList.toggle('bg-primary/5', checkbox.checked);
    };

    // `row` is the <label> itself and `checkbox` is one of its descendants, so
    // clicking anywhere in the row already toggles the checkbox natively (the
    // browser's default label-activation behavior). Manually flipping
    // `checkbox.checked` here too used to double-toggle it back to its
    // original value on every click — just react to the resulting `change`.
    checkbox.addEventListener('change', sync);
    sync();
  }

  _setError(root, message) {
    const el = root.querySelector('[data-address-form-error]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('hidden', !message);
  }

  _collectPayload(root, mode, address) {
    const getValue = (field) =>
      (root.querySelector(`[data-field="${field}"]`)?.value || '').trim();
    const countrySelect = root.querySelector(
      '[data-country-slot] select, select[name="country"]',
    );
    const provinceSelect = root.querySelector(
      '[data-province-slot] select, select[name="province"]',
    );
    const checkbox = root.querySelector('[data-default-checkbox]');

    const payload = {
      first_name: getValue('first_name'),
      last_name: getValue('last_name'),
      company: getValue('company'),
      address1: getValue('address1'),
      address2: getValue('address2'),
      city: getValue('city'),
      zip: getValue('zip'),
      phone: getValue('phone'),
      country: (countrySelect?.value || '').trim(),
      province: (provinceSelect?.value || '').trim(),
      is_default: Boolean(checkbox?.checked),
    };

    if (mode === 'edit' && address) payload.id = address.id;
    return payload;
  }

  _bindDialogSubmit(root, dialogModal, mode, address) {
    const form = root.querySelector('[data-address-form]');
    const submitBtn = root.querySelector('[data-address-form-submit]');
    if (!form || !submitBtn) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!form.reportValidity()) return;

      this._setError(root, '');
      submitBtn.classList.add('loading');
      submitBtn.disabled = true;

      const payload = this._collectPayload(root, mode, address);
      const ok = await this._submitAddress(payload);

      if (ok) {
        await this._fetchList();
        dialogModal.close();
      } else {
        this._setError(root, this._config.genericError || '');
        submitBtn.classList.remove('loading');
        submitBtn.disabled = false;
      }
    });
  }

  async _submitAddress(payload) {
    try {
      const res = await fetch(
        this.dataset.updateEndpoint || '/apps/lensza/update-address',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      const body = await res.json().catch(() => ({}));
      return res.ok && body.status === 'ok';
    } catch (e) {
      return false;
    }
  }

  async _setDefault(id, btn) {
    if (!btn) return;
    btn.disabled = true;
    try {
      const res = await fetch(
        this.dataset.setDefaultEndpoint || '/apps/lensza/set-default-address',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.status === 'ok') await this._fetchList();
    } finally {
      btn.disabled = false;
    }
  }

  async _deleteAddress(id, btn) {
    if (!window.confirm(this._config.deleteConfirm || '')) return;
    if (!btn) return;
    btn.disabled = true;
    try {
      const res = await fetch(
        this.dataset.deleteEndpoint || '/apps/lensza/delete-address',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.status === 'ok') await this._fetchList();
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('dashboard-addresses')) {
  customElements.define('dashboard-addresses', DashboardAddresses);
}
